import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, BookOpen, Award, CheckCircle, AlertTriangle, Lightbulb, ArrowRight, Play } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { analyzeSkillGaps } from '../../../services/openaiService';

const SkillGapAnalysis = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [analysisData, setAnalysisData] = useState(null);
  const [error, setError] = useState(null);

  const mockAnalysisData = {
    profileCompleteness: 78,
    skillsAnalyzed: 12,
    gapsIdentified: 5,
    recommendedCourses: 8,
    currentSkills: [
    { name: 'React.js', level: 85, status: 'strong' },
    { name: 'JavaScript', level: 80, status: 'strong' },
    { name: 'HTML/CSS', level: 90, status: 'strong' },
    { name: 'Git', level: 70, status: 'moderate' },
    { name: 'Tailwind CSS', level: 75, status: 'moderate' }],

    skillGaps: [
    {
      id: 1,
      skill: 'TypeScript',
      importance: 'High',
      demandScore: 92,
      reason: 'Required by 78% of frontend developer jobs',
      impact: 'Learning TypeScript can increase job matches by 45%',
      timeToLearn: '4-6 weeks'
    },
    {
      id: 2,
      skill: 'Next.js',
      importance: 'High',
      demandScore: 88,
      reason: 'Popular framework for React applications',
      impact: 'Opens opportunities in 35% more companies',
      timeToLearn: '3-4 weeks'
    },
    {
      id: 3,
      skill: 'Testing (Jest/React Testing Library)',
      importance: 'Medium',
      demandScore: 75,
      reason: 'Essential for senior developer roles',
      impact: 'Improves candidacy for senior positions',
      timeToLearn: '2-3 weeks'
    },
    {
      id: 4,
      skill: 'GraphQL',
      importance: 'Medium',
      demandScore: 70,
      reason: 'Growing adoption in modern applications',
      impact: 'Differentiates from other candidates',
      timeToLearn: '3-4 weeks'
    },
    {
      id: 5,
      skill: 'Docker',
      importance: 'Low',
      demandScore: 65,
      reason: 'Useful for deployment and DevOps',
      impact: 'Adds versatility to skill set',
      timeToLearn: '2-3 weeks'
    }],

    recommendedCourses: [
    {
      id: 1,
      title: 'TypeScript for React Developers',
      provider: 'Udemy',
      rating: 4.8,
      students: '125,430',
      duration: '12 hours',
      level: 'Intermediate',
      price: '₹499',
      skills: ['TypeScript', 'React'],
      thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_10af01abc-1768238504170.png",
      thumbnailAlt: 'Computer screen showing TypeScript code with modern IDE interface',
      link: '#'
    },
    {
      id: 2,
      title: 'Complete Next.js Developer Bootcamp',
      provider: 'Coursera',
      rating: 4.7,
      students: '89,250',
      duration: '18 hours',
      level: 'Intermediate',
      price: '₹799',
      skills: ['Next.js', 'React', 'SSR'],
      thumbnail: "https://images.unsplash.com/photo-1676279170337-c838813c7e39",
      thumbnailAlt: 'Modern workspace with laptop displaying web development code',
      link: '#'
    },
    {
      id: 3,
      title: 'React Testing Masterclass',
      provider: 'Pluralsight',
      rating: 4.6,
      students: '45,680',
      duration: '8 hours',
      level: 'Intermediate',
      price: '₹599',
      skills: ['Jest', 'React Testing Library', 'TDD'],
      thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1510a3747-1764920069842.png",
      thumbnailAlt: 'Developer working on testing code with multiple monitors',
      link: '#'
    },
    {
      id: 4,
      title: 'GraphQL with React: Complete Guide',
      provider: 'Udemy',
      rating: 4.7,
      students: '67,890',
      duration: '15 hours',
      level: 'Intermediate',
      price: '₹699',
      skills: ['GraphQL', 'Apollo Client', 'React'],
      thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_16c81d403-1766015221955.png",
      thumbnailAlt: 'Code editor showing GraphQL queries and schema definitions',
      link: '#'
    }]

  };

  useEffect(() => {
    const performSkillAnalysis = async () => {
      try {
        setIsAnalyzing(true);
        setError(null);

        // User profile data (in real app, this would come from user's actual profile)
        const userProfile = {
          currentSkills: [
          { name: 'React.js', level: 85 },
          { name: 'JavaScript', level: 80 },
          { name: 'HTML/CSS', level: 90 },
          { name: 'Git', level: 70 },
          { name: 'Tailwind CSS', level: 75 }],

          careerGoals: 'Senior Frontend Developer',
          targetRole: 'Frontend Developer',
          experience: '2 years'
        };

        // Market demands data
        const marketDemands = {
          topSkills: ['TypeScript', 'Next.js', 'React', 'Testing', 'GraphQL', 'Docker'],
          industryTrends: ['Full-stack capabilities', 'Modern frameworks', 'Testing proficiency'],
          salaryImpact: {
            TypeScript: '+15%',
            'Next.js': '+12%',
            Testing: '+10%'
          }
        };

        // Call OpenAI API for real skill gap analysis
        const aiResults = await analyzeSkillGaps(userProfile, marketDemands);

        // Use AI results with mock course data (course recommendations would need a separate API)
        setAnalysisData({
          ...aiResults,
          recommendedCourses: mockAnalysisData?.recommendedCourses
        });
      } catch (err) {
        setError(err?.message);
        // Fallback to mock data on error
        setAnalysisData(mockAnalysisData);
      } finally {
        setIsAnalyzing(false);
      }
    };

    performSkillAnalysis();
  }, []);

  const getImportanceColor = (importance) => {
    if (importance === 'High') return 'text-red-600 bg-red-50 border-red-200';
    if (importance === 'Medium') return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  const getSkillLevelColor = (level) => {
    if (level >= 80) return 'bg-green-500';
    if (level >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (isAnalyzing) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex flex-col items-center justify-center space-y-6">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
            <Target className="w-8 h-8 text-purple-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Analyzing Your Skills</h3>
            <p className="text-gray-600">Comparing your profile against market demands...</p>
            <div className="mt-4 space-y-2 text-sm text-gray-500">
              <p className="flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-purple-600 rounded-full animate-pulse"></span>
                Evaluating current skill levels
              </p>
              <p className="flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-purple-600 rounded-full animate-pulse"></span>
                Identifying skill gaps
              </p>
              <p className="flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-purple-600 rounded-full animate-pulse"></span>
                Finding relevant courses
              </p>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="space-y-6">
      {/* Analysis Overview */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Skill Gap Analysis Complete</h3>
            <p className="text-sm text-gray-600">Personalized insights based on your profile and market trends</p>
          </div>
        </div>

        {error &&
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> {error} Showing sample data.
            </p>
          </div>
        }

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Profile Strength</p>
            <p className="text-2xl font-bold text-gray-900">{analysisData?.profileCompleteness}%</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Skills Analyzed</p>
            <p className="text-2xl font-bold text-blue-600">{analysisData?.skillsAnalyzed}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Gaps Found</p>
            <p className="text-2xl font-bold text-orange-600">{analysisData?.gapsIdentified}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Courses Found</p>
            <p className="text-2xl font-bold text-green-600">{analysisData?.recommendedCourses?.length || 0}</p>
          </div>
        </div>
      </div>

      {/* Current Skills */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          Your Current Skills
        </h3>
        <div className="space-y-3">
          {analysisData?.currentSkills?.map((skill, idx) =>
          <div key={idx}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">{skill?.name}</span>
                <span className="text-sm text-gray-600">{skill?.level}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                className={`h-2 rounded-full ${getSkillLevelColor(skill?.level)}`}
                style={{ width: `${skill?.level}%` }}>
              </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Skill Gaps */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            Identified Skill Gaps
          </h3>
        </div>

        {analysisData?.skillGaps?.map((gap) =>
        <div key={gap?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-lg font-semibold text-gray-900">{gap?.skill}</h4>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getImportanceColor(gap?.importance)}`}>
                    {gap?.importance} Priority
                  </span>
                </div>
                <p className="text-gray-600 mb-3">{gap?.reason}</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-sm">
                    <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700"><strong>Impact:</strong> {gap?.impact}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <Target className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700"><strong>Market Demand:</strong> {gap?.demandScore}% of relevant jobs</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <BookOpen className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700"><strong>Time to Learn:</strong> {gap?.timeToLearn}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="w-20 h-20 rounded-full border-4 border-gray-200 bg-gray-50 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{gap?.demandScore}</p>
                    <p className="text-xs text-gray-600">Demand</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recommended Courses */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-600" />
            Recommended Courses
          </h3>
          <Button variant="outline" size="sm">
            View All Courses
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {analysisData?.recommendedCourses?.map((course) =>
          <div key={course?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <img
              src={course?.thumbnail}
              alt={course?.thumbnailAlt}
              className="w-full h-40 object-cover" />

              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-base font-semibold text-gray-900 flex-1">{course?.title}</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">{course?.provider}</p>
                
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                  <span className="flex items-center gap-1">
                    <Award className="w-4 h-4 text-yellow-500" />
                    {course?.rating}
                  </span>
                  <span>{course?.students} students</span>
                  <span>{course?.duration}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {course?.skills?.map((skill, idx) =>
                <span key={idx} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full border border-purple-200">
                      {skill}
                    </span>
                )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-gray-900">{course?.price}</span>
                  <Button size="sm">
                    <Play className="w-4 h-4 mr-2" />
                    Enroll Now
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Learning Path */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <ArrowRight className="w-6 h-6 text-blue-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Suggested Learning Path</h3>
            <p className="text-sm text-gray-600">Follow this sequence for optimal skill development</p>
          </div>
        </div>
        <div className="space-y-3">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Master TypeScript</p>
                <p className="text-sm text-gray-600">4-6 weeks • High Priority</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Learn Next.js Framework</p>
                <p className="text-sm text-gray-600">3-4 weeks • High Priority</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Practice Testing with Jest</p>
                <p className="text-sm text-gray-600">2-3 weeks • Medium Priority</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default SkillGapAnalysis;